import type {
	TopologyFaceBoundaryMemberEvidence,
	TopologyFaceReferenceSourceDescriptor,
	TopologyFaceRelocationCandidate,
	TopologyFaceRelocationHost,
	TopologyFaceRelocationResult,
} from '../../../../../types/reference/sketch-entity-reference/TopologyFaceReferenceSource';

/**
 * Convert Entities 面来源重定位服务。
 *
 * 面只负责界定本轮应存在的边成员集合。调用方已经把候选限制在同一个稳定
 * topology owner/body 内，因此已存在的语义名直接表示当前来源面谱系，不再使用
 * 曲面类型、生成特征或拓扑索引补猜身份。
 */
export class TopologyFaceRelocationService {
	/**
	 * 在当前 topology owner 中重定位持久面来源。
	 *
	 * @param descriptor 持久化面来源。
	 * @param host 当前历史位置的 topology host。
	 * @param boundaryEvidence 已有面成员提供的稳定边界依据。
	 * @returns 找到来源面谱系时返回全部当前物理后代。
	 */
	public relocate(
		descriptor: TopologyFaceReferenceSourceDescriptor,
		host: TopologyFaceRelocationHost,
		boundaryEvidence: readonly TopologyFaceBoundaryMemberEvidence[] = [],
	): TopologyFaceRelocationResult {
		const faces = host.listFaces();
		if (descriptor.locator.semanticName) {
			const semanticMatches = faces.filter(
				(face) => face.semanticName === descriptor.locator.semanticName,
			);
			if (semanticMatches.length > 0) {
				return {
					status: 'resolved',
					faces: this.sortFaces(semanticMatches),
				};
			}

			/*
			 * 当前快照可能缺少生成层尚未覆盖的语义字段。只有几何持久引用唯一命中，
			 * 且候选本身没有其它已知语义时才允许兼容；明确属于其它谱系的面禁止回退绑定。
			 */
			const persistentMatch = this.findUniquePersistentRefMatch(
				descriptor,
				faces.filter((face) => !face.semanticName),
			);
			return persistentMatch
				? {
					status: 'resolved',
					faces: [persistentMatch],
				}
				: {status: 'failed'};
		}

		const preferredMemberFaces = this.findPreferredTopologyMemberFaces(
			descriptor,
			faces,
		);
		const preferredPersistentMatch = this.findUniquePersistentRefMatch(
			descriptor,
			preferredMemberFaces,
		);
		if (preferredPersistentMatch) {
			return {
				status: 'resolved',
				faces: [preferredPersistentMatch],
			};
		}

		const preferredBoundaryMatch = this.findUniqueConstrainedBoundaryMatch(
			descriptor,
			preferredMemberFaces,
			boundaryEvidence,
		);
		if (preferredBoundaryMatch) {
			return {
				status: 'resolved',
				faces: [preferredBoundaryMatch],
			};
		}

		const persistentMatch = this.findUniquePersistentRefMatch(descriptor, faces);
		if (persistentMatch) {
			return {
				status: 'resolved',
				faces: [persistentMatch],
			};
		}

		/*
		 * 原结果成员仍存在时，Boolean 也可能已经把原面迁移到同一 Body 血缘的其它成员。
		 * 跨成员恢复必须同时满足局部地址、曲面类型、生成特征与稳定边界，任何单项都不能
		 * 独立决定面身份；这样既允许合法迁移，也不会把重复 index 的其它特征面误认为来源面。
		 */
		const constrainedBoundaryMatch = this.findUniqueConstrainedBoundaryMatch(
			descriptor,
			faces,
			boundaryEvidence,
		);
		if (constrainedBoundaryMatch) {
			return {
				status: 'resolved',
				faces: [constrainedBoundaryMatch],
			};
		}

		return {status: 'failed'};
	}

	/**
	 * 提取当前结果对象对应的 checkpoint 面候选。
	 *
	 * 该成员只为局部 index/subEntityId 提供作用域；语义面谱系仍在全体同血缘成员
	 * 中解析，因而不会截断一个面分裂出的多个物理后代，也不会阻止后续合并。
	 *
	 * @param descriptor 持久化面来源。
	 * @param faces 当前同血缘候选面。
	 * @returns 当前结果体成员内的候选；成员不存在时返回空数组。
	 */
	private findPreferredTopologyMemberFaces(
		descriptor: TopologyFaceReferenceSourceDescriptor,
		faces: readonly TopologyFaceRelocationCandidate[],
	): TopologyFaceRelocationCandidate[] {
		const resultObjectId = descriptor.owner.resultObjectId?.trim();
		if (!resultObjectId) {
			return [];
		}
		return faces.filter((face) => face.topologyMemberId === resultObjectId);
	}

	/**
	 * 在候选集合中按几何持久引用查找唯一面。
	 *
	 * `subEntityId` 当前由 topology index 派生，不能作为跨重建身份；没有唯一
	 * `persistentRef` 时宁可返回失败，也不按曲面类型、生成特征或旧索引猜测。
	 *
	 * @param descriptor 持久化面来源。
	 * @param faces 已由 owner/body 限定的候选面。
	 * @returns 唯一命中的候选；身份不足或歧义时返回 null。
	 */
	private findUniquePersistentRefMatch(
		descriptor: TopologyFaceReferenceSourceDescriptor,
		faces: readonly TopologyFaceRelocationCandidate[],
	): TopologyFaceRelocationCandidate | null {
		const persistentRef = descriptor.locator.persistentRef;
		if (!persistentRef) {
			return null;
		}
		const matches = faces.filter(
			(face) => face.persistentRef === persistentRef,
		);
		return matches.length === 1 ? matches[0] ?? null : null;
	}

	/**
	 * 使用同面身份与稳定边界锚点恢复几何指纹已经变化的来源面。
	 *
	 * `persistentRef` 当前包含面几何中心，合法尺寸变化会使它失效。该兼容路径同时要求：
	 * 1. 候选仍位于同一面索引和子实体身份；
	 * 2. 曲面类型与生成特征一致；
	 * 3. 至少一条旧边的 persistentRef 保持，或者完整历史外环 subEntityId
	 *    集合仍存在于候选面边界中；
	 * 4. 全部条件只能命中一个候选。
	 *
	 * Face 候选的 boundaryEdges 同时包含外环与内环，因此完整外环身份采用包含关系，
	 * 不能与候选面的全部边界直接比较集合相等。任一条件不足都返回 null，禁止仅按
	 * index 或曲面类型猜测。
	 *
	 * @param descriptor 持久化面来源。
	 * @param faces 同 owner/body 内的当前候选。
	 * @param boundaryEvidence 已有面成员边界依据。
	 * @returns 唯一受约束候选；证据不足时返回 null。
	 */
	private findUniqueConstrainedBoundaryMatch(
		descriptor: TopologyFaceReferenceSourceDescriptor,
		faces: readonly TopologyFaceRelocationCandidate[],
		boundaryEvidence: readonly TopologyFaceBoundaryMemberEvidence[],
	): TopologyFaceRelocationCandidate | null {
		const locator = descriptor.locator;
		if (
			!locator.subEntityId
			|| !locator.surfaceKind
			|| !locator.owningFeatureId
		) {
			return null;
		}
		const stableBoundaryEvidence = boundaryEvidence.filter((evidence) => (
			typeof evidence.persistentRef === 'string'
			&& typeof evidence.curveKind === 'string'
		));
		const completeBoundarySubEntityEvidence = boundaryEvidence.filter((evidence) => (
			typeof evidence.subEntityId === 'string'
			&& typeof evidence.curveKind === 'string'
		));
		const hasCompleteBoundarySubEntityEvidence = (
			completeBoundarySubEntityEvidence.length > 0
			&& completeBoundarySubEntityEvidence.length === boundaryEvidence.length
			&& new Set(
				completeBoundarySubEntityEvidence.map(evidence => evidence.subEntityId),
			).size === completeBoundarySubEntityEvidence.length
		);
		if (
			stableBoundaryEvidence.length <= 0
			&& !hasCompleteBoundarySubEntityEvidence
		) {
			return null;
		}

		const matches = faces.filter((face) => (
			face.faceIndex === locator.faceIndex
			&& face.subEntityId === locator.subEntityId
			&& face.surfaceKind === locator.surfaceKind
			&& face.owningFeatureId === locator.owningFeatureId
			&& (
				stableBoundaryEvidence.some((evidence) => (
					face.boundaryEdges.some((edge) => (
						edge.persistentRef === evidence.persistentRef
						&& edge.curveKind === evidence.curveKind
					))
				))
				|| (
					hasCompleteBoundarySubEntityEvidence
					&& completeBoundarySubEntityEvidence.every((evidence) => (
						face.boundaryEdges.some((edge) => (
							edge.subEntityId === evidence.subEntityId
							&& edge.curveKind === evidence.curveKind
						))
					))
				)
			)
		));
		return matches.length === 1 ? matches[0] ?? null : null;
	}

	/**
	 * 生成与 host 枚举顺序无关的后代面顺序。
	 *
	 * @param faces 当前语义来源面的物理后代。
	 * @returns 独立且稳定排序的候选数组。
	 */
	private sortFaces(
		faces: readonly TopologyFaceRelocationCandidate[],
	): TopologyFaceRelocationCandidate[] {
		return [...faces].sort((left, right) => {
			const leftKey = left.persistentRef
				?? left.subEntityId
				?? `face-index:${left.faceIndex}`;
			const rightKey = right.persistentRef
				?? right.subEntityId
				?? `face-index:${right.faceIndex}`;
			return leftKey.localeCompare(rightKey);
		});
	}

}
